import React, { useReducer, useRef } from 'react';

import styled from '@emotion/styled';

// Type definition
export enum SegmentedViewReducerActionType {
  CLICK_SEGMENT = 'segment-clicked',
}

export interface ISegmentedViewReducerActionType {
  type: SegmentedViewReducerActionType;
  payload: any;
}

interface IsegmentedViewControllerTitle {
  title: string;
}

type State = { index: number };

interface ISegmentedViewData {
  children: React.ReactNode[] | React.ReactNode | any;
  state?: State;
  handleStateChange?: (...args: any) => void;
}

interface ISegmentedViewController extends ISegmentedViewData {
  segmentedViewControllerTitle: IsegmentedViewControllerTitle[];
}

// Reducer

function segmentedViewReducer(
  state: any,
  action: ISegmentedViewReducerActionType,
) {
  switch (action.type) {
    case SegmentedViewReducerActionType.CLICK_SEGMENT:
      return { ...action.payload };
    default:
      return state;
  }
}

// Components

export const SegmentedView: React.FC<Pick<ISegmentedViewData, 'children'>> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(segmentedViewReducer, { index: 0 });

  const handleStateChange = (index: any) =>
    dispatch({
      type: SegmentedViewReducerActionType.CLICK_SEGMENT,
      payload: { index },
    });

  return (
    <SegmentedViewContainer>
      {React.Children.map(children, (child: any) => {
        if (child.type === SegmentedViewController) {
          return React.cloneElement(child, {
            ...child.props,
            state,
            handleStateChange,
          });
        } else if (child.type === SegmentedViewData) {
          return React.cloneElement(child, { ...child.props, state });
        }
      })}
    </SegmentedViewContainer>
  );
};

export const SegmentedViewController: React.FC<
  Omit<ISegmentedViewController, 'children'>
> = ({ segmentedViewControllerTitle, state, handleStateChange }) => {
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <SegmentedViewControllerWrapper ref={containerRef}>
      {segmentedViewControllerTitle.map((data, index) => (
        <SegmentButton
          ref={(el) => {
            segmentRefs.current[index] = el;
          }}
          className={state?.index === index ? 'activeSegment' : ''}
          onClick={() => handleStateChange!(index)}
          key={index}
        >
          {data.title}
        </SegmentButton>
      ))}
    </SegmentedViewControllerWrapper>
  );
};

export const SegmentedViewData: React.FC<
  Omit<ISegmentedViewData, 'handleStateChange'>
> = ({ children, state }) => {
  return (
    <SegmentData>
      {React.Children.map(children, (child: any, index) => {
        const computedClass = state?.index === index ? 'showSegment' : '';
        return React.cloneElement(child, {
          ...child.props,
          className: computedClass,
        });
      })}
    </SegmentData>
  );
};

// Styles

const SegmentData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* border: 1px solid red; */

  & > * {
    display: none;
    width: 100%;
  }

  & > .showSegment {
    display: flex;

    @media (max-width: 884px) {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    @media (max-width: 480px) {
      width: 100%;
    }
  }

  & > .showSegment > * {
    @media (max-width: 480px) {
      width: 100%;
    }
  }
`;

const SegmentedViewControllerWrapper = styled.div`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  /* padding: 4px; */
  /* border: 1px solid red; */

  @media (max-width: 884px) {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 12px;
    column-gap: 24px;
    border-bottom: none;
  }
`;

const SegmentButton = styled.div`
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 14px;
  font-size: 14px;
  padding: 14px 0px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.neutralGray};
  cursor: pointer;

  &:hover {
  }

  /* border: 1px solid red; */

  &.activeSegment {
    color: ${({ theme }) => theme.palette.logirateBlack};
    color: #5c5963;
    font-weight: 700;
    border-bottom: 2px solid #5b91f5;

    /* @media (max-width: 884px) {
      border-radius: 6px;
    } */
  }
`;

const SegmentedViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 27px;
  width: 100%;
  height: fit-content;

  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: 3rem;
  }
`;
